import {
  parseMultilineContent,
  parseSingleLineContent,
} from "@repo/app-types/content/plaintext";
import type { MessageTemplateSettingsSectionType } from "@repo/app-types/entities/messageTemplate";
import {
  isInterviewSettingsSectionType,
  MessageTemplateType,
  messageTemplateTypesBySettingsSectionType,
  MessageTemplateTypeToLabel,
  noVariablesMessageTemplateTypes,
  standardRecordingSummaryTemplateBody,
} from "@repo/app-types/entities/messageTemplate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useEffect, useState } from "react";
import { useCreateMessageTemplateMutation } from "../../../api/entities/messageTemplate.js";
import { MessageTemplateForm } from "./messageTemplateForm.js";

export interface MessageTemplateCreateDialogProps {
  messageTemplateSectionType: MessageTemplateSettingsSectionType;
  isOpen: boolean;
  onClose: () => void;
}

export interface MessageTemplateValues {
  subject: string;
  body: string;
}

export const MessageTemplateCreateDialog: FC<
  MessageTemplateCreateDialogProps
> = (props) => {
  // Get the types of message templates we support for this section of settings.
  // Interivew message templates can be outreach/advanced/rejected.
  // Call recorder message templates can be recording email or recording summary.
  const messageTemplateTypeOptions = messageTemplateTypesBySettingsSectionType[
    props.messageTemplateSectionType
  ].map((t) => ({
    label: MessageTemplateTypeToLabel[t],
    value: t,
  }));

  const isInterviewSectionType = isInterviewSettingsSectionType(
    props.messageTemplateSectionType,
  );

  const defaultMessageTemplateType = isInterviewSectionType
    ? MessageTemplateType.Outreach
    : MessageTemplateType.RecordingSummary;

  const [createMessageTemplate, { isLoading: isCreating }] =
    useCreateMessageTemplateMutation();

  const [subject, setSubject] = useState("");

  const [type, setType] = useState<MessageTemplateType | null>(
    defaultMessageTemplateType,
  );

  const [body, setBody] = useState("");

  // Set the body to the standard template when type is RecordingSummary
  useEffect(() => {
    if (type === MessageTemplateType.RecordingSummary) {
      setBody(standardRecordingSummaryTemplateBody);
    }
  }, [type]);

  // TODO: This is an annoying pattern. Find a better way to do this.
  const resetState = () => {
    setType(defaultMessageTemplateType);
    setSubject("");
    // Set body based on default type
    setBody("");
  };

  const onClose = () => {
    resetState();
    props.onClose();
  };

  const onCreate = async () => {
    if (!type) {
      return;
    }

    // Do not parse variables if template does not support them
    const parsesVariables = !noVariablesMessageTemplateTypes.includes(type);

    await createMessageTemplate({
      type,
      subject: parseSingleLineContent(subject, {
        parsesVariables,
      }),
      body: parseMultilineContent(body, {
        parsesVariables,
      }),
    }).unwrap();
    onClose();
  };

  const onTypeChange = (newType: MessageTemplateType) => {
    setSubject("");
    setBody("");
    setType(newType);
  };

  const isValid = !!subject.trim() && !!body.trim() && type;

  return (
    <Modal isOpen={props.isOpen} onCancel={onClose} size={Size.LARGE}>
      <ModalHeader onClose={onClose}>
        {isInterviewSectionType ? "New message template" : "New template"}
      </ModalHeader>
      <div className="my-4 space-y-4">
        <div className="flex flex-col space-y-2">
          <div className="w-[250px]">
            <SimpleSelect
              label={isInterviewSectionType ? "Message type" : "Template type"}
              value={type ?? defaultMessageTemplateType}
              placeholder="Select a type"
              options={messageTemplateTypeOptions}
              onChange={onTypeChange}
            />
          </div>
        </div>
        <MessageTemplateForm
          subject={subject}
          body={body}
          type={type ?? defaultMessageTemplateType}
          onSubjectChange={setSubject}
          onBodyChange={setBody}
        />
      </div>
      <div className="flex justify-end space-x-2">
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          isLoading={isCreating}
          isDisabled={!isValid}
          onClick={onCreate}
        >
          {isInterviewSectionType
            ? "Create message template"
            : "Create template"}
        </Button>
      </div>
    </Modal>
  );
};
